import { useCallback, useMemo, useState } from "react"

import { SectionTacticMap } from "@/app/report/common/constructor.ts"
import { ActivityDetail } from "@/types/activity.ts"
import {
  CONTENT_WIDTH_MM,
  MAX_CONTENT_HEIGHT_MM,
  mmPerPx,
} from "@/app/report/common/export/paginate"
import { PDF_PREVIEW_WIDTH_PX } from "@/app/report/common/export/constants"

import TacticMapSection from "../TacticMapSection"

/** 한 페이지에 담을 수 있는 콘텐츠 높이(px) — paginate 의 페이지 예산과 같은 기준을 쓴다 */
const PAGE_CONTENT_PX = Math.floor(
  MAX_CONTENT_HEIGHT_MM / mmPerPx(CONTENT_WIDTH_MM, PDF_PREVIEW_WIDTH_PX),
)
/** 섹션 제목·여백이 차지하는 높이 */
const SECTION_CHROME_PX = 121
/** 액션 표 헤더(제목 2줄 + 단위줄) — 미리보기 DOM 실측값 */
const TABLE_HEADER_PX = 58
/** 액션 표 행 높이 — 미리보기 DOM 실측값 */
const TABLE_ROW_PX = 44

/** 맵과 표가 나란히 놓이는 줄의 높이 */
const ROW_AREA_PX = PAGE_CONTENT_PX - SECTION_CHROME_PX

/**
 * 코트 폭. 원본 348x540 비율이 고정이라 폭을 키우면 높이가 1.55배로 따라온다.
 * 코트를 줄인 만큼 오른쪽 액션 표가 넓어진다.
 */
const MAP_WIDTH_PX = 267
const MAP_BASE_WIDTH_PX = 348
const MAP_BASE_HEIGHT_PX = 540
const MAP_SCALE = MAP_WIDTH_PX / MAP_BASE_WIDTH_PX
const MAP_HEIGHT_PX = Math.round(MAP_BASE_HEIGHT_PX * MAP_SCALE)

/** 한 페이지에 실을 수 있는 액션 표 행 수 */
export const ACTION_ROWS_PER_PAGE = Math.max(
  1,
  Math.floor((ROW_AREA_PX - TABLE_HEADER_PX) / TABLE_ROW_PX),
)

/**
 * 액션맵(전술 맵) PDF 출력용 래퍼.
 *
 * 액션맵은 CHUNK_TYPE.ACTION_MAP 이라 한 페이지를 통째로 쓴다. 예전에는 1/3 페이지에
 * 넣느라 표에 max-height 를 걸었고, 그 결과 9행만 남고 나머지 선수가 조용히 사라졌다.
 *
 * 표가 한 페이지에 안 들어가면 페이지를 이어 쓰되, **코트는 페이지마다 다시 그린다** —
 * 표만 이어지면 뒤 페이지에서 어느 경기의 어느 위치인지 알 수 없기 때문이다.
 *
 * 전체 행 수는 자식이 데이터를 받아야 알 수 있으므로 onRowCount 로 올려받아 페이지 수를
 * 정한다(첫 렌더는 1페이지 → 행 수를 알면 필요한 만큼 늘어난다).
 */
export default function PrintableTacticMapSection({
  reportSection,
  activity,
  idfSessions,
  idfPlayers,
}: {
  reportSection: SectionTacticMap
  activity: ActivityDetail
  idfSessions: number[]
  idfPlayers: number[]
}) {
  const [rowCount, setRowCount] = useState(0)

  // useEffect 의존성으로 들어가므로 참조가 안정적이어야 한다
  const handleRowCount = useCallback((count: number) => setRowCount(count), [])

  const pages = useMemo(() => Math.max(1, Math.ceil(rowCount / ACTION_ROWS_PER_PAGE)), [rowCount])

  return (
    <>
      <style>{`
        .PrintableTacticMapSection .TacticMapSection .TacticMapSectionMenuContainer {
          display: none !important;
        }
        .PrintableTacticMapSection .TacticMapSection .TimeRangeSliderContainer {
          display: none !important;
        }
        .PrintableTacticMapSection .TacticMapSection .ReportSectionCont {
          box-shadow: none !important;
        }

        /* 섹션 좌우 여백(20px)까지 되찾아 코트와 표가 전체 폭을 나눠 쓴다 */
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-container {
          width: calc(100% + 40px);
          margin-left: -20px;
          margin-right: -20px;
          align-items: flex-start;
          justify-content: flex-start;
          gap: 8px;
        }

        /* 코트를 키운다. CSS 폭으로는 못 줄이거나 키운다 — 컨테이너 폭을 JS 가 인라인으로
           박고 내부 SVG 에 viewBox 가 없어 좌표가 절대 픽셀이라, 폭만 바꾸면 배경만 변하고
           선·점이 어긋난다. transform 은 배경 SVG 와 좌표를 함께 배율 적용하므로 정확하다.
           transform 은 레이아웃 크기를 안 바꾸므로 자리는 직접 확보한다. */
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-components {
          flex: 0 0 ${MAP_WIDTH_PX}px;
          height: ${MAP_HEIGHT_PX}px;
          /* 플렉스 항목 기본값 min-width:auto 는 축소 전 콘텐츠 폭(348px)을 최소로 삼아
             flex-basis 를 무시한다. 0 으로 풀어야 줄인 만큼 표가 넓어진다. */
          min-width: 0;
        }
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-components > * {
          transform: scale(${MAP_SCALE});
          transform-origin: top left;
        }

        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table {
          flex: 1 1 auto;
          min-width: 0;
        }

        /* compactTable 의 최소 폭(글자 폭 + 14)에 맞춰 셀 좌우 패딩을 좁힌다.
           화면 기본값(16px)을 그대로 두면 글자가 잘린다. */
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table-component .MuiDataGrid-columnHeader,
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table-component .MuiDataGrid-cell {
          padding-right: 6px !important;
          padding-left: 6px !important;
        }

        /* 데이터가 없어도 표가 자리를 잡도록 최소 높이를 준다 (빈 상태 147px 은 너무 납작). */
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table-component,
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table-component .MuiDataGrid-virtualScroller {
          min-height: 220px;
        }

        /* 화면용 스타일은 표를 코트 높이(540px)에 맞춰 잘라내고 안에서 스크롤시킨다.
           출력에는 스크롤이 없으므로 그대로 두면 잘린 행이 그냥 사라진다 — 둘 다 푼다.
           행이 넘칠 만큼 많으면 위에서 페이지를 나눠 넘긴다. */
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table {
          overflow: visible !important;
        }
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table-component {
          max-height: none !important;
        }

        /* 행·컬럼은 DOM 에 전부 있다(disableVirtualization=true). 내부 스크롤러의 overflow 만
           풀면 잘리지 않고 캡처된다. 높이 제한은 두지 않는다 — 넘칠 만큼 행이 많으면
           위에서 페이지를 나눠 넘기기 때문이다. */
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table-component .MuiDataGrid-main,
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table-component .MuiDataGrid-topContainer,
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table-component .MuiDataGrid-virtualScroller {
          overflow: visible !important;
        }
        .PrintableTacticMapSection .TacticMapSection .tactic-map-middle-table-component .MuiDataGrid-scrollbar {
          display: none !important;
        }
      `}</style>

      {Array.from({ length: pages }, (_, page) => (
        <div
          key={`tactic-map-page-${page}`}
          className="renewal-pdf-container PrintableTacticMapSection"
          style={{ overflow: "hidden", background: "white" }}>
          <TacticMapSection
            compactTable
            reportSection={reportSection}
            activity={activity}
            idfSessions={idfSessions}
            idfPlayers={idfPlayers}
            loading={false}
            tableRowRange={{
              start: page * ACTION_ROWS_PER_PAGE,
              end: (page + 1) * ACTION_ROWS_PER_PAGE,
            }}
            onRowCount={page === 0 ? handleRowCount : undefined}
          />
        </div>
      ))}
    </>
  )
}
